import { useState, useEffect } from "react";
import { useAuth } from "@/auth-provider";
import {
  Menu,
  CloudSun,
  Search,
  MapPin,
  Bell,
  User,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/theme-toggle";
import { SidebarNav } from "./sidebar-nav";
import { SidebarAlerts } from "./sidebar-alerts";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { useLocation } from "@/components/location-provider";
import { useUnit } from "@/components/unit-provider";
import { useLocation as useRRLocation, useNavigate, Link } from "react-router-dom";
import { useGeocode } from "@/lib/use-geocode";

const pageTitles: Record<string, string> = {
  "/": "Dashboard",
  "/forecast": "Forecast",
  "/map": "Map",
  "/insights": "Insights",
  "/alerts": "Alerts",
  "/preparedness": "Preparedness",
  "/clothing": "Clothing",
  "/compare": "Compare",
  "/astronomy": "Astronomy",
  "/notifications": "Notifications",
  "/account": "Account",
  "/about": "About",
};

export function Topbar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const { locations, current, setCurrent } = useLocation();
  const { unit, setUnit } = useUnit();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const rrLocation = useRRLocation();
  const pathname = rrLocation.pathname;

  const { results: geoResults } = useGeocode(query, { enabled: showResults || query.length >= 2 });
  const results = geoResults.slice(0, 5);

  const handleSearch = (q: string) => {
    setQuery(q);
    setShowResults(q.length >= 2);
  };

  useEffect(() => {
    fetch(`/api/weather?lat=${current.lat}&lon=${current.lon}`)
      .then((r) => r.json())
      .then((d) => setAlertCount((d.weather?.alerts || []).length))
      .catch(() => {});
  }, [current.lat, current.lon]);

  const breadcrumbItems = [];
  if (pathname !== "/") {
    breadcrumbItems.push({ label: pageTitles[pathname] || pathname.slice(1), href: pathname });
  }

  return (
    <header role="banner" className="sticky top-0 z-40 flex h-14 items-center gap-3 bg-background/60 backdrop-blur-xl border-b border-border/20 px-4 lg:px-6">
      {/* Mobile menu */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border/40 bg-background/60 hover:bg-accent/50 transition-colors lg:hidden cursor-pointer">
          <Menu className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </SheetTrigger>
        <SheetContent side="left" className="w-60 p-0 bg-sidebar/90 backdrop-blur-2xl">
          <SheetTitle className="flex h-14 items-center gap-2.5 border-b border-border/20 px-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
              <CloudSun className="h-4 w-4 text-primary" />
            </div>
            <span className="font-bold tracking-tight text-sm">Vertali</span>
          </SheetTitle>
          <div className="py-3">
            <SidebarNav onNavigate={() => setOpen(false)} />
            <SidebarAlerts />
          </div>
        </SheetContent>
      </Sheet>

      {/* Breadcrumbs */}
      {breadcrumbItems.length > 0 && (
        <Breadcrumb items={breadcrumbItems} className="hidden sm:flex" />
      )}

      {/* Location Selector */}
      <div className="hidden sm:flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5 text-primary/70" />
        <select
          value={current.key}
          onChange={(e) => {
            const loc = locations.find((l) => l.key === e.target.value);
            if (loc) setCurrent(loc);
          }}
          aria-label="Select location"
          className="rounded-lg border border-border/40 bg-background/50 backdrop-blur-sm px-2.5 py-1.5 text-xs font-semibold outline-none focus:border-primary/50 transition-colors"
        >
          {locations.map((loc) => (
            <option key={loc.key} value={loc.key}>
              {loc.name}, {loc.country}
            </option>
          ))}
        </select>
      </div>

      {/* Search */}
      <div className="relative flex-1 max-w-xs hidden sm:block">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground/50" />
        <Input
          placeholder="Search city..."
          aria-label="Search for a city"
          className="pl-8 h-8 text-xs rounded-lg border-border/30 bg-background/50 backdrop-blur-sm"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
          onFocus={() => results.length > 0 && setShowResults(true)}
        />
        {showResults && results.length > 0 && (
          <div className="absolute top-9 left-0 right-0 z-50 overflow-hidden rounded-xl border border-border/40 bg-popover/95 backdrop-blur-xl shadow-xl shadow-black/10">
            {results.map((r, i) => (
              <button
                key={i}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-muted/50 transition-colors"
                onMouseDown={() => {
                  setCurrent({ key: r.name.toLowerCase().replace(/\s/g, "-"), name: r.name, lat: r.lat, lon: r.lon, country: r.country || "" });
                  setQuery("");
                  setShowResults(false);
                }}
              >
                <MapPin className="h-3.5 w-3.5 text-muted-foreground/50" />
                <span>{r.name}, {r.country}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1" />

      {/* Alert notification */}
      {alertCount > 0 && (
        <Link to="/alerts" aria-label={`${alertCount} active weather alerts`} className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-muted/50 transition-colors">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <Badge variant="destructive" size="sm" className="absolute -top-0.5 -right-0.5 px-1 py-0 min-w-[16px] justify-center">
            {alertCount}
          </Badge>
        </Link>
      )}

      {/* Unit Toggle */}
      <div className="flex items-center rounded-lg border border-border/30 bg-background/40 backdrop-blur-sm overflow-hidden text-[10px] font-semibold" role="radiogroup" aria-label="Temperature unit">
        <button
          role="radio"
          aria-checked={unit === "C"}
          onClick={() => setUnit("C")}
          className={`px-2.5 py-1.5 transition-all duration-200 ${unit === "C" ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
        >
          °C
        </button>
        <button
          onClick={() => setUnit("CF")}
          className={`px-2.5 py-1.5 transition-all duration-200 border-x border-border/30 ${unit === "CF" ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
        >
          C/F
        </button>
        <button
          onClick={() => setUnit("F")}
          className={`px-2.5 py-1.5 transition-all duration-200 ${unit === "F" ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-muted/30"}`}
        >
          °F
        </button>
      </div>

      <ThemeToggle />

      {/* Auth */}
      <div className="flex items-center gap-2 pl-2 border-l border-border/30">
        {user ? (
          <>
            <div className="hidden md:flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Hi,</span>
              <span className="font-medium truncate max-w-[100px]">{user.name || "User"}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/account")}>
              Account
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate("/login"); }}>
              Sign Out
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={() => navigate("/login")}>
            Sign In
          </Button>
        )}
      </div>
    </header>
  );
}
